import React, { useEffect, useMemo, useState } from 'react';
import { getUserDetailsBaseUrl } from '../utils/chatIdentity';

const Icon = ({ children, className = 'h-4 w-4' }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);

const TABS = ['Overview', 'Diagnostics', 'Services', 'Documents', 'Chats'];

const statusToneClass = (tone) => (
    tone === 'resolved' ? 'bg-[#eef6fc] text-[#0b74b5]'
        : tone === 'recommended' ? 'bg-[#eef6fc] text-[#0b74b5]'
        : tone === 'progress' ? 'bg-[#fff8e8] text-[#936000]'
        : 'bg-[#f1f5f9] text-[#5f728a]'
);

const fmtMonthYear = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};
const fmtDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

// Right-side vehicle detail drawer with Overview / Diagnostics / Services / Documents / Chats.
// Diagnostics, maintenance and chats come from props; service history + documents are fetched here.
export default function VehicleDetailDrawer({
    vehicle,
    isOpen,
    onClose,
    distanceUnit = 'mi',
    phone,
    isActive,
    openCount = 0,
    photoUrl,
    diagnostics = [],
    maintenanceItems = [],
    chats = [],
    onSetActive,
    onStartDiagnostic,
    onAddPhoto,
    onLogService,
    onAddDocument,
    onEditVehicle,
    onOpenTicket,
    onViewReport,
    onOpenChat,
    onOpenDocument,
}) {
    const [tab, setTab] = useState('Overview');
    const [services, setServices] = useState([]);
    const [documents, setDocuments] = useState([]);

    const vehicleId = vehicle?.vehicle_id;

    useEffect(() => { if (isOpen) setTab('Overview'); }, [isOpen, vehicleId]);

    // Fetch service history + documents when the drawer opens for a vehicle.
    useEffect(() => {
        if (!isOpen || !phone || !vehicleId) return;
        let cancelled = false;
        (async () => {
            try {
                const [svcResp, docResp] = await Promise.all([
                    fetch(`${getUserDetailsBaseUrl()}/api/service-history/${encodeURIComponent(phone)}?vehicle_id=${encodeURIComponent(vehicleId)}`).then((r) => r.json()).catch(() => ({})),
                    fetch(`${getUserDetailsBaseUrl()}/api/documents/${encodeURIComponent(phone)}`).then((r) => r.json()).catch(() => ({})),
                ]);
                if (cancelled) return;
                setServices(Array.isArray(svcResp?.entries) ? svcResp.entries : []);
                const allDocs = Array.isArray(docResp?.documents) ? docResp.documents : [];
                setDocuments(allDocs.filter((d) => String(d?.vehicle_id) === String(vehicleId)));
            } catch (_) {
                if (!cancelled) { setServices([]); setDocuments([]); }
            }
        })();
        return () => { cancelled = true; };
    }, [isOpen, phone, vehicleId]);

    const mileageText = useMemo(() => {
        const v = vehicle?.odometer ?? vehicle?.total_distance_miles ?? vehicle?.mileage ?? vehicle?.miles;
        const n = v != null ? Number(String(v).replace(/,/g, '')) : null;
        return Number.isFinite(n) && n > 0 ? `${Math.round(n).toLocaleString()} ${distanceUnit}` : '—';
    }, [vehicle, distanceUnit]);

    if (!vehicle) return null;

    const year = vehicle.year || '';
    const make = vehicle.make || '';
    const model = [vehicle.model, vehicle.trim].filter(Boolean).join(' ') || vehicle.model || 'Vehicle';

    const Tile = ({ icon, value, label, onClick }) => (
        <button type="button" onClick={onClick} className="flex items-center gap-3 rounded-2xl border border-[#e5eef6] bg-[#f8fbfe] px-4 py-4 text-left transition hover:border-[#9fcdf0]">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-[#0b74b5] shadow-sm">{icon}</span>
            <span>
                <span className="block text-xl font-black leading-none text-[#0b1b2e]">{value}</span>
                <span className="mt-1 block text-xs font-semibold text-[#5f728a]">{label}</span>
            </span>
        </button>
    );

    const Detail = ({ label, value, mono }) => (
        <div className="min-w-0">
            <p className="text-xs font-semibold text-[#5f728a]">{label}</p>
            <p className={`mt-1 truncate text-sm font-black text-[#182233] ${mono ? 'font-mono' : ''}`} title={value || ''}>{value || '—'}</p>
        </div>
    );

    return (
        <div className={`fixed inset-0 z-[90] ${isOpen ? 'visible' : 'invisible pointer-events-none'}`}>
            <div className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'}`} onClick={onClose} />
            <div className={`absolute right-0 top-0 bottom-0 flex w-full max-w-[520px] flex-col overflow-hidden bg-white shadow-[-20px_0_80px_rgba(0,0,0,0.12)] transition-transform duration-300 will-change-transform ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                {/* Header */}
                <div className="shrink-0 px-6 pt-6">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <p className="text-sm font-black text-[#0b74b5]">{year} · {make}</p>
                            <h2 className="mt-0.5 truncate text-[26px] font-black leading-tight text-[#0b1b2e]">{model}</h2>
                            <p className="mt-2 flex items-center gap-2 text-sm font-semibold text-[#5f728a]">
                                <Icon className="h-4 w-4"><circle cx="12" cy="12" r="9" /><path d="M12 12 8 8M12 7v.01" /></Icon>
                                {mileageText}
                                <span className="text-[#c6d3e0]">/</span>
                                {isActive ? (
                                    <span className="font-black text-[#087a43]">Active</span>
                                ) : (
                                    <button type="button" onClick={() => onSetActive?.(vehicle)} className="font-black text-[#0b74b5] hover:underline">Set as active</button>
                                )}
                            </p>
                        </div>
                        <button type="button" onClick={onClose} aria-label="Close" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#e5eef6] text-[#5f728a] transition hover:bg-[#f2f9ff]">
                            <Icon className="h-4 w-4"><path d="M18 6 6 18M6 6l12 12" /></Icon>
                        </button>
                    </div>
                    <button type="button" onClick={() => onStartDiagnostic?.(vehicle)} className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#2541b2] px-5 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-[#1e3596]">
                        <Icon className="h-4 w-4"><path d="M14.7 6.3a4 4 0 0 0-5.4 5.4l-6 6 2 2 6-6a4 4 0 0 0 5.4-5.4l-2.3 2.3-2-2z" /></Icon>
                        Start Diagnostic
                    </button>
                    {/* Tabs */}
                    <div className="mt-5 flex gap-5 border-b border-[#e5eef6] overflow-x-auto">
                        {TABS.map((t) => (
                            <button key={t} type="button" onClick={() => setTab(t)}
                                className={`-mb-px whitespace-nowrap border-b-2 pb-3 text-sm font-black transition ${
                                    tab === t ? 'border-[#0b74b5] text-[#0b74b5]' : 'border-transparent text-[#5f728a] hover:text-[#1f3350]'
                                }`}>
                                {t}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Body */}
                <div className="min-h-0 flex-1 overflow-y-auto bg-[#fbfdff] px-6 py-5">
                    {tab === 'Overview' && (
                        <div className="space-y-6">
                            <div>
                                <button type="button" onClick={() => onAddPhoto?.(vehicle)} className="inline-flex items-center gap-2 rounded-xl border border-[#d8e6f3] bg-white px-4 py-2.5 text-sm font-black text-[#1f3350] transition hover:bg-[#f2f9ff]">
                                    <Icon className="h-4 w-4"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></Icon>
                                    Add photo
                                </button>
                                <p className="mt-2 text-xs font-semibold text-[#8aa1b5]">Session only · Clears on reload · JPG, PNG, WebP up to 5 MB</p>
                                {photoUrl && <img src={photoUrl} alt={model} className="mt-3 h-40 w-full rounded-xl object-cover" />}
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <Tile value={openCount} label="Open diagnostics" onClick={() => setTab('Diagnostics')}
                                    icon={<Icon className="h-4 w-4"><path d="M14.7 6.3a4 4 0 0 0-5.4 5.4l-6 6 2 2 6-6a4 4 0 0 0 5.4-5.4l-2.3 2.3-2-2z" /></Icon>} />
                                <Tile value={documents.length} label="Documents" onClick={() => setTab('Documents')}
                                    icon={<Icon className="h-4 w-4"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></Icon>} />
                            </div>
                            <div>
                                <p className="mb-2 text-base font-black text-[#0b1b2e]">Vehicle details</p>
                                <div className="grid grid-cols-2 gap-x-6 gap-y-4 rounded-2xl border border-[#e5eef6] bg-white px-5 py-5">
                                    <Detail label="VIN" value={vehicle.vin} mono />
                                    <Detail label="Engine" value={vehicle.engine} />
                                    <Detail label="Transmission" value={vehicle.transmission} />
                                    <Detail label="Drivetrain" value={vehicle.drivetrain} />
                                </div>
                            </div>
                            <div>
                                <p className="mb-2 text-base font-black text-[#0b1b2e]">Upcoming maintenance</p>
                                {maintenanceItems.length === 0 ? (
                                    <p className="text-sm font-semibold text-[#5f728a]">No upcoming maintenance.</p>
                                ) : (
                                    <div className="space-y-2">
                                        {maintenanceItems.map((m) => (
                                            <div key={m.key} className="rounded-2xl border border-[#e5eef6] bg-white px-4 py-3">
                                                <p className="text-sm font-black text-[#182233]">{m.title}</p>
                                                {m.dueLabel ? <p className="mt-0.5 text-xs font-semibold text-[#5f728a]">{m.dueLabel}</p> : null}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div>
                                <p className="mb-2 text-base font-black text-[#0b1b2e]">Planned Mods</p>
                                {(!Array.isArray(vehicle.modifications) || vehicle.modifications.length === 0) ? (
                                    <p className="text-sm font-semibold text-[#5f728a]">No planned mods logged for this vehicle.</p>
                                ) : (
                                    <div className="space-y-2">
                                        {vehicle.modifications.map((mod, idx) => (
                                            <div key={mod.mod_id ?? idx} className="rounded-2xl border border-[#e5eef6] bg-white px-4 py-3">
                                                <p className="text-sm font-black text-[#182233]">{mod.name || 'Modification'}</p>
                                                {mod.category ? <p className="mt-0.5 text-xs font-semibold text-[#5f728a]">{mod.category}</p> : null}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {tab === 'Diagnostics' && (
                        <div className="space-y-3">
                            {diagnostics.length === 0 ? (
                                <p className="text-sm font-semibold text-[#5f728a]">No diagnostics for this vehicle yet.</p>
                            ) : diagnostics.map((d) => (
                                <div key={d.id} className="rounded-2xl border border-[#e5eef6] bg-white px-4 py-4">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <p className="text-sm font-black text-[#182233]">{d.title}</p>
                                            {d.ageLabel ? <p className="mt-0.5 text-xs font-semibold text-[#5f728a]">{d.ageLabel}</p> : null}
                                        </div>
                                        {d.statusLabel ? <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-black ${statusToneClass(d.statusTone)}`}>{d.statusLabel}</span> : null}
                                    </div>
                                    <button type="button" onClick={() => (d.action === 'report' ? onViewReport?.(d.raw) : onOpenTicket?.(d.raw))}
                                        className="mt-3 inline-flex items-center gap-1.5 rounded-xl border border-[#d8e6f3] bg-white px-3.5 py-2 text-sm font-black text-[#1f3350] transition hover:bg-[#f2f9ff]">
                                        {d.action === 'report' ? 'View report' : 'Continue'}
                                        <Icon className="h-3.5 w-3.5"><path d="M7 17 17 7M8 7h9v9" /></Icon>
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {tab === 'Services' && (
                        <div>
                            <button type="button" onClick={() => onLogService?.(vehicle)} className="inline-flex items-center gap-2 rounded-xl border border-[#d8e6f3] bg-white px-4 py-2.5 text-sm font-black text-[#1f3350] transition hover:bg-[#f2f9ff]">
                                <Icon className="h-4 w-4"><path d="M14.7 6.3a4 4 0 0 0-5.4 5.4l-6 6 2 2 6-6a4 4 0 0 0 5.4-5.4l-2.3 2.3-2-2z" /></Icon>
                                Log service
                            </button>
                            {maintenanceItems.length > 0 && (
                                <div className="mt-5">
                                    <p className="mb-2 text-base font-black text-[#0b1b2e]">Scheduled maintenance</p>
                                    <div className="divide-y divide-[#eef3f8]">
                                        {maintenanceItems.map((m) => (
                                            <div key={m.key} className="py-3">
                                                <p className="text-[15px] font-black text-[#182233]">{m.title}</p>
                                                {m.dueLabel ? <p className="mt-0.5 text-xs font-semibold text-[#5f728a]">{m.dueLabel}</p> : null}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="mt-5">
                                <p className="mb-2 text-base font-black text-[#0b1b2e]">Service history</p>
                                {services.length === 0 ? (
                                    <p className="text-sm font-semibold text-[#5f728a]">No service records yet.</p>
                                ) : (
                                    <div className="divide-y divide-[#eef3f8]">
                                        {services.map((s) => {
                                            const miles = s.odometer_mileage != null ? `${Number(s.odometer_mileage).toLocaleString()} miles` : '';
                                            const when = fmtMonthYear(s.performed_at);
                                            const meta = [when, miles].filter(Boolean).join(' · ');
                                            return (
                                                <div key={s.service_event_id} className="py-3">
                                                    <p className="text-[15px] font-black text-[#182233]">{s.title}</p>
                                                    {meta ? <p className="mt-0.5 text-xs font-semibold text-[#5f728a]">{meta}</p> : null}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {tab === 'Documents' && (
                        <div className="space-y-2.5">
                            {documents.length === 0 ? (
                                <p className="text-sm font-semibold text-[#5f728a]">No documents yet. Use “Add document” on the card.</p>
                            ) : documents.map((doc) => {
                                const kind = String(doc.document_kind || 'document').replace(/_/g, ' ');
                                const when = fmtDate(doc.issued_at || doc.created_at || doc.updated_at);
                                return (
                                    <button key={doc.doc_id} type="button" onClick={() => onOpenDocument?.(doc)}
                                        className="flex w-full items-center gap-3 rounded-2xl border border-[#e5eef6] bg-white px-4 py-3.5 text-left transition hover:border-[#9fcdf0]">
                                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#eef6fc] text-[#0b74b5]"><Icon className="h-4 w-4"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></Icon></span>
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate text-sm font-black text-[#182233]">{doc.original_filename || doc.name || 'Document'}</span>
                                            <span className="block text-xs font-semibold capitalize text-[#5f728a]">{[kind, when].filter(Boolean).join(' · ')}</span>
                                        </span>
                                        <Icon className="h-4 w-4 shrink-0 text-[#0b74b5]"><path d="M7 17 17 7M8 7h9v9" /></Icon>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {tab === 'Chats' && (
                        <div className="space-y-3">
                            {chats.length === 0 ? (
                                <p className="text-sm font-semibold text-[#5f728a]">No chats for this vehicle yet.</p>
                            ) : chats.map((c) => (
                                <div key={c.id} className="rounded-2xl border border-[#e5eef6] bg-white px-4 py-4">
                                    <div className="flex items-start justify-between gap-3">
                                        <p className="flex min-w-0 items-center gap-2 text-sm font-black text-[#182233]">
                                            <Icon className="h-4 w-4 shrink-0 text-[#0b74b5]"><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" /></Icon>
                                            <span className="truncate">{c.title}</span>
                                        </p>
                                        {c.statusLabel ? <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-black ${statusToneClass(c.statusTone)}`}>{c.statusLabel}</span> : null}
                                    </div>
                                    {c.snippet ? <p className="mt-2 line-clamp-2 text-sm font-medium text-[#5f728a]">{c.snippet}</p> : null}
                                    {c.action !== false && (
                                        <button type="button" onClick={() => onOpenChat?.(c.raw)} className="mt-3 inline-flex items-center gap-1.5 rounded-xl border border-[#d8e6f3] bg-white px-3.5 py-2 text-sm font-black text-[#1f3350] transition hover:bg-[#f2f9ff]">
                                            Continue
                                            <Icon className="h-3.5 w-3.5"><path d="M7 17 17 7M8 7h9v9" /></Icon>
                                        </button>
                                    )}
                                    {c.timeLabel ? <p className="mt-2 text-xs font-semibold text-[#8aa1b5]">{c.timeLabel}</p> : null}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
